<template>
  <div class="asc-user-container">
    <asc-user-management-template
      :values="mappedUsers"
      :on-open="onRowClick"
      :add-user="handleAddUserDialog"
      :grid-filters="gridFilters"
      :disabled="addUserDisabled"
    ></asc-user-management-template>

    <asc-user-dialog
      :show-dialog="showEditDialog && !apiPageFailure.length"
      :user="singleUser"
      :edit="edit"
      :loading="dialogLoading"
      :edit-button-loading="submitEditLoading && !apiPageFailure.length"
      :new-button-loading="submitNewLoading && !apiPageFailure.length"
      :title="title"
      :on-close="onCloseEditDialog"
      :role="roles"
      :airlines="activeAirlines"
      :report-types="reportTypes"
      :email-subscription-types="userEmailSubscriptionTypes"
      :user-email-subscriptions="userEmailSubscriptions"
      :company="companyList"
      :master-models="masterModels"
      :analytic-modules="mappedAnalyticModules"
      :is-user-reader="isUserReader"
      :groups="rolesData"
      @submitEdit="submitEdit"
    ></asc-user-dialog>

    <asc-bulk-user-mgmt-dialog
      :show-dialog="showAddDialog"
      :airlines="activeAirlines"
      :roles="roles"
      :report-types="reportTypes"
      :companies="companyList"
      :on-close="onCloseAddDialog"
      :submit-loading="addDialogLoading"
      :groups="rolesData"
      @onSubmit="submitNew"
    ></asc-bulk-user-mgmt-dialog>

    <asc-confirmation
      v-if="toastDisplayed"
      :definition="definition"
      :on-close="closeToastNotification"
    ></asc-confirmation>
  </div>
</template>

<script>
import AscBulkUserMgmtDialog from '@/components/molecules/AscBulkUserMgmtDialog'
import AscConfirmation from '@/components/molecules/AscConfirmation'
import AscUserDialog from '@/components/molecules/AscUserDialog'
import AscUserManagementTemplate from '@/components/templates/AscUserManagementTemplate'
import {
  ADMIN_CONSTS as AC,
  ANALYTIC_MODULE_CONSTS as AMC,
  LOOKUP_CONSTS as LC,
  USER_CONSTS as UC,
  AIRLINE_MANAGEMENT_CONSTS as AOC,
  ROLE_MANAGEMENT_CONSTS as RMC,
} from '@/stores/consts'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'AscUserManagementContainer',

  components: {
    AscUserManagementTemplate,
    AscUserDialog,
    AscConfirmation,
    AscBulkUserMgmtDialog,
  },

  props: {
    gridFilters: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      showEditDialog: false,
      showAddDialog: false,
      dialogLoading: false,
      addDialogLoading: false,
      title: '',
      edit: false,
      toastDisplayed: false,
      definition: [
        {
          mode: 'toast',
          type: 'success',
          text: 'Changes Saved',
          dismissible: true,
          hasToastAction: false,
          autoClose: true,
        },
      ],
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(AC.NAMESPACE, [
      'users',
      'userEmailSubscriptions',
      'userEmailSubscriptionTypes',
      'allUsersLoading',
      'newUserToast',
      'editUserToast',
      'submitNewLoading',
      'submitEditLoading',
      'singleUser',
      'failedUsers',
      'reportTypes',
    ]),
    ...mapState(RMC.NAMESPACE, ['rolesData']),
    ...mapState(AOC.NAMESPACE, ['allAirlines']),
    ...mapGetters(AC.NAMESPACE, ['mappedUsers']),
    ...mapGetters(AMC.NAMESPACE, ['mappedAnalyticModules']),
    ...mapState(LC.NAMESPACE, ['roles', 'masterModels', 'permissions']),
    ...mapState(UC.NAMESPACE, ['companyList', 'newCompany']),
    ...mapGetters(UC.NAMESPACE, ['isUserReader']),
    addUserDisabled() {
      return this.isUserReader
    },
    activeAirlines() {
      return this.allAirlines.filter(item => item.isActive)
    },
  },

  created() {
    this.getUserData()
  },

  methods: {
    ...mapActions(AC.NAMESPACE, [
      AC.GET_USERS,
      AC.CREATE_USER,
      AC.SET_USER,
      AC.SET_USER_EMAIL_SUBSCRIPTIONS,
      AC.GET_USER_EMAIL_SUBSCRIPTION_TYPES,
      AC.GET_USER_EMAIL_SUBSCRIPTIONS,
      AC.GET_SINGLE_USER,
      AC.DELETE_USER_EMAIL_SUBSCRIPTION,
      AC.GET_REPORT_TYPES,
    ]),
    ...mapActions(LC.NAMESPACE, [
      LC.GET_ROLES,
      LC.GET_OPERATORS,
      LC.GET_MASTER_MODELS,
      LC.GET_PERMISSIONS,
    ]),
    ...mapActions(UC.NAMESPACE, [UC.GET_COMPANY_LIST, UC.SET_COMPANY_LIST]),
    ...mapActions(AMC.NAMESPACE, [AMC.GET_ANALYTIC_MODULES]),
    ...mapActions(AOC.NAMESPACE, [AOC.GET_ALL_AIRLINES]),

    async getUserData() {
      await Promise.all([
        this[AC.GET_USERS](),
        this[UC.GET_COMPANY_LIST](),
        this[LC.GET_ROLES](),
        this[AC.GET_REPORT_TYPES](),
        this[LC.GET_OPERATORS](),
        // this[AC.GET_USER_EMAIL_SUBSCRIPTION_TYPES](),
        this[LC.GET_MASTER_MODELS](),
        this[AMC.GET_ANALYTIC_MODULES](),
        this[LC.GET_PERMISSIONS](),
        this[AOC.GET_ALL_AIRLINES](),
      ])
    },
    handleAddUserDialog() {
      this.showAddDialog = true
    },
    async onRowClick(data) {
      this.title = 'User Management - Edit User'
      this.edit = true
      this.showEditDialog = true
      this.dialogLoading = true
      await Promise.all([
        this[AC.GET_SINGLE_USER](data.userId),
        // this[AC.GET_USER_EMAIL_SUBSCRIPTIONS](data.userId),
      ])
      this.dialogLoading = false
    },
    onCloseEditDialog() {
      this.showEditDialog = false
      this.edit = false
    },
    onCloseAddDialog() {
      this.showAddDialog = false
    },
    async submitNew(data) {
      this.addDialogLoading = true
      const mappedUsers = []
      // loop through list of users to add
      // add new company if necessary
      for (let index = 0; index < data.length; index++) {
        let userCompany = this.companyList.find(
          company => company.name === data[index].company,
        )?.id
        if (!userCompany) {
          const newCompany = {
            name: data[index].company,
          }
          await Promise.all([this[UC.SET_COMPANY_LIST](newCompany)])
          userCompany = this.newCompany.id
        }
        const user = {
          ...data[index],
          companyId: userCompany,
          displayName: `${data[index].firstName.trim()} ${data[
            index
          ].lastName.trim()}`,
        }
        mappedUsers.push(user)
      }

      await Promise.all([this[AC.CREATE_USER](mappedUsers)])

      if (this.failedUsers.length) {
        this.definition[0].text = `Users that failed to create: ${this.failedUsers
          .map(item => item.displayName)
          .join()}`
        this.definition[0].autoClose = false
      } else {
        this.definition[0].text = 'User(s) created successfully'
        this.definition[0].autoClose = true
      }

      this.addDialogLoading = false
      this.showAddDialog = false
      this.title = ''
      this.edit = false
      this.toastDisplayed = true
    },
    async submitEdit({ user }) {
      if (isNaN(user.companyId)) {
        const newCompany = {
          name: user.companyId,
        }
        await Promise.all([this[UC.SET_COMPANY_LIST](newCompany)])
        user.companyId = this.newCompany.id
      }

      /*
      remove comment lines when we add email subs tab back

      const deletedSubscriptions = this.userEmailSubscriptions[
        user.userId
      ].filter(function (userEmailSub) {
        return (
          emailSubscriptions.filter(function (sub) {
            return sub.id === userEmailSub.id
          }).length === 0
        )
      })

      await Promise.all(
        deletedSubscriptions.map(async subscription => {
          await this[AC.DELETE_USER_EMAIL_SUBSCRIPTION]({
            userId: user.userId,
            subscriptionId: subscription.id,
          })
        }),
      )


      await Promise.all([
        this[AC.SET_USER_EMAIL_SUBSCRIPTIONS]({
          userId: user.userId,
          subscriptions: emailSubscriptions,
        }),
      ])
      */

      await Promise.all([this[AC.SET_USER](user)])
      if (this.editUserToast) {
        this.definition[0].text = 'Changes Saved'
        this.definition[0].autoClose = true
        this.toastDisplayed = true
      }
      this.showEditDialog = false
      this.title = ''
      this.edit = false
    },
    closeToastNotification() {
      this.toastDisplayed = false
    },
  },
}
</script>
